import { ANDROID_HREF } from '../constants';
import { removeSpecialCharacters } from '../utils';

// ---- Internal Helper Functions ---- //

// Replaces all occurrences of a specific refinement value in the array
// with a new value while preserving the original order.
const replaceRefinement = (refinements, from, to) => {
  return refinements.map((refinement) => (refinement === from ? to : refinement));
};

// Normalizes a breadcrumb object by ensuring it has default properties and values.
const normalizeBreadcrumb = (crumb) => {
  return {
    id: null,
    name: null,
    label: null,
    refinementKey: null,
    dimensionName: 'Category',
    ...crumb // what is needed from the breadcrumb??? :sob:
  };
};

// Applies price refinement logic and returns a formatted price part and updated refinements.
const applyPriceRefinement = (processedRefinements) => {
  const priceRefinement = processedRefinements.find((refinement) => refinement.includes('$'));
  if (!priceRefinement) return { pricePart: '', updatedRefinements: processedRefinements };

  const [lowerRaw, upperRaw] = priceRefinement
    .split('-')
    .map((part) => part.replace(/\$/g, '').replace(/,/g, '').trim());

  const lower = Number(lowerRaw);
  const upper = Number(upperRaw);

  if (Number.isNaN(lower) || Number.isNaN(upper)) {
    return { pricePart: '', updatedRefinements: processedRefinements };
  }

  const updatedRefinements = [...processedRefinements];
  const index = updatedRefinements.indexOf(priceRefinement);

  if (index !== -1) {
    updatedRefinements.splice(index, 1); // remove from original position
  }

  if (lower === 0 && upper > 0) {
    return { pricePart: `Under $${upper.toLocaleString()}`, updatedRefinements };
  }

  // Only add back if not an "Under" case
  updatedRefinements.push(priceRefinement);
  return { pricePart: '', updatedRefinements };
};

// Builds a breadcrumb label string by joining refinements and categories with ' - ',
// and appends the price part if provided, followed by a trailing dash.
const buildBreadcrumbLabel = (refinements = [], category = '', pricePart = '') => {
  const label = [...refinements, category].join(' - ');
  if (!label && !pricePart) return '';
  return `${label}${pricePart ? ' ' + pricePart : ''} -`;
};

// Returns a formatted string of categories and refinements.
// Handles price refinements, normalizes certain labels, and adjusts output based on the number of categories.
const getRefinedCategories = (categories, refinements) => {
  if (categories.length === 0) {
    return refinements.join(', ');
  }

  const processedRefinements = [...refinements];
  let { pricePart, updatedRefinements } = applyPriceRefinement(processedRefinements);

  const normalizedRefinements = updatedRefinements
    .map((refinement) => (refinement === '4 & Up' ? 'Highly Rated' : refinement))
    .filter(Boolean);

  if (categories.length === 1) {
    const base = normalizedRefinements.length
      ? normalizedRefinements.join(', ')
      : categories[0];
    return [base, pricePart].filter(Boolean).join(' ').trim();
  }

  const lastCategory = categories[categories.length - 1];
  const base = normalizedRefinements.length
    ? [...normalizedRefinements].reverse().join(', ')
    : '';
  return [base, lastCategory, pricePart].filter(Boolean).join(' ').trim();
};

// Returns the first category as the department, or an empty string if categories is empty.
const getDepartment = (categories = []) => categories[0] || '';

// Returns a string with a formatted category label for the title,
// applying special cases for 'Sofa Beds' and 'Console Tables' (with no refinements).
const getCategoryTitle = (categories, refinements) => {
  const CONSOLE_TABLE_CATEGORY = 'Console Tables';
  const APPENDED_CONSOLE_TABLE_CATEGORY = 'Console Tables & Sofa Tables';
  const SOFA_BEDS = 'Sofa Beds';
  const APPENDED_SOFA_BEDS = 'Sofa Beds & Sleeper Sofas';

  const isEmptyRefinements = refinements?.length === 0;

  const lastCategory = categories[categories.length - 1];
  if (!lastCategory) return '';

  if (lastCategory === SOFA_BEDS) {
    return APPENDED_SOFA_BEDS;
  }

  if (lastCategory === CONSOLE_TABLE_CATEGORY && isEmptyRefinements) {
    return APPENDED_CONSOLE_TABLE_CATEGORY;
  }

  return lastCategory;
};

// Builds the SEO title tag for browse pages based on categories, refinements, and search state.
const getBrowseTitleTag = ({ pathWithQueryParams, categories, refinements, searchVal }) => {
  const isBrowseInSearch = pathWithQueryParams?.includes('Ntt-');
  if (!Array.isArray(categories) || categories.length === 0) return 'Browse The Home Depot';
  if (isBrowseInSearch) return `Search Results for ${searchVal} at The Home Depot`;

  const categoryTitle = getCategoryTitle(categories, refinements);
  const hasFurniture = categories.indexOf('Furniture') !== -1;

  const processedRefinements = [...refinements];

  const {
    pricePart,
    updatedRefinements: baseRefinements
  } = applyPriceRefinement(processedRefinements);

  const hasBestRated = baseRefinements.includes('Best Rated');
  const hasFourAndUp = baseRefinements.includes('4 & Up');
  const hasPickUpToday = baseRefinements.includes('Pick Up Today');

  let updatedRefinements = [...baseRefinements];

  if (!hasBestRated) {
    if (hasFourAndUp) {
      updatedRefinements = replaceRefinement(updatedRefinements, '4 & Up', 'Highly Rated');
    } else if (hasPickUpToday && !hasFurniture) {
      updatedRefinements = replaceRefinement(updatedRefinements, 'Pick Up Today', 'In Stock Near Me');
    }
  }

  const breadCrumbLabel = buildBreadcrumbLabel(
    updatedRefinements,
    categoryTitle,
    pricePart
  );

  return breadCrumbLabel ? `${breadCrumbLabel} The Home Depot` : 'The Home Depot';
};

// ---- Exported Functions ---- //

/**
 * Processes an array of breadcrumb objects and separates them into categories and refinements.
 *
 * @param {Array<Object>} breadcrumbs - The breadcrumb objects to process.
 * @returns {[Array<string>, Array<string>]} - An array containing two arrays: categories and refinements.
 */
export const processBreadcrumbs = (breadcrumbs) => {
  const categories = [];
  const refinements = [];
  if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
    return [categories, refinements];
  }
  breadcrumbs.forEach((crumb) => {
    const dimension = crumb?.dimensionName;
    const label = crumb?.label;
    if (dimension === 'Category') {
      categories.push(label);
      return;
    }
    if (dimension === 'Review Rating' && label === '5') {
      refinements.push('Best Rated');
      return;
    }
    if (label !== 'Home') {
      refinements.push(label);
    }
  });

  return [categories, refinements];
};

/**
 * Generates a description for browse and search pages based on page type, categories, refinements, and other parameters
 *
 * @param {string} pageType - The type of page ('b' for browse, other for search).
 * @param {Array<string>} categories - Array of category names.
 * @param {Array<string>} refinements - Array of refinement labels.
 * @param {string} searchVal - The search value or keyword.
 * @param {boolean} isContentful - Whether the description should be overridden for Contentful.
 * @param {boolean} isContentPageOnly - Whether to use the parsed template description for content pages only.
 * @param {string} parsedTemplateDescription - The description from the parsed template,
 * used if `isContentPageOnly` is true.
 * @returns {string} - The generated description string.
 */
export const getDescription = (
  pageType,
  categories = [],
  refinements = [],
  searchVal,
  isContentful,
  isContentPageOnly = false,
  parsedTemplateDescription
) => {

  const browseAppliances = categories[0] === 'Appliances' && pageType === 'b';
  if (isContentPageOnly && !browseAppliances) {
    return parsedTemplateDescription;
  }

  if (pageType !== 'b') {
    return `Search Results for ${searchVal} at The Home Depot`;
  }

  // Special case: 'Pick Up Today' (only when NOT Furniture and we have a category)
  const hasPickUpToday = refinements.includes('Pick Up Today');
  const hasFurniture = categories.includes('Furniture');
  if (hasPickUpToday && !hasFurniture && categories.length > 0) {
    const withoutPickUp = refinements.filter((refinement) => refinement !== 'Pick Up Today');
    const refinementsText = [...withoutPickUp].reverse().filter(Boolean).join(', ');
    const lastCategory = categories[categories.length - 1];

    // Build 'Find <refinements> <lastCategory> ready to be picked up today...'
    const prefix = refinementsText ? `Find ${refinementsText} ` : 'Find ';
    return `${prefix}${lastCategory} ready to be picked up today at your local Home Depot store.`;
  }

  const refinedCategories = getRefinedCategories(categories, refinements);
  const department = getDepartment(categories);

  // Special case: Major Appliances description (top-level Appliances)
  if (browseAppliances) {
    return `Browse our online aisle of ${refinedCategories}. `
      + `Shop The Home Depot for all your ${department} and DIY needs.`;
  }

  // Contentful override (only for the default path, matching original behavior)
  if (isContentful) {
    // eslint-disable-next-line max-len
    return `Shop ${refinedCategories} and more at The Home Depot. We offer free delivery, in-store and curbside pick-up for most items.`;
  }

  // Default description (with optional department suffix)
  // eslint-disable-next-line max-len
  const description = `Get free shipping on qualified ${refinedCategories} products or Buy Online Pick Up in Store today`
    + (department ? ` in the ${department} Department` : '');

  return `${description}.`;
};

/**
 * Generates the SEO title tag for browse and search pages.
 *
 * @param {string} pageType - The type of page ('b' for browse, 's' for search, others for default).
 * @param {Array<string>} [categories=[]] - Category names for the page, used to generate the title.
 * @param {Array<string>} [refinements=[]] - Array of refinement labels.
 * @param {string} searchVal - The search value or keyword.
 * @param {string} pathWithQueryParams - The URL path with query parameters.
 * @param {boolean} isContentPageOnly - Whether to use the parsed template title for content pages only.
 * @param {string} parsedTemplateTitle - The title from the parsed template, used if `isContentPageOnly` is true.
 * @returns {string} - The generated title tag string.
 */
export const getTitleTag = ({
  pageType,
  categories,
  refinements,
  searchVal,
  pathWithQueryParams,
  isContentPageOnly,
  parsedTemplateTitle
}) => {
  if (isContentPageOnly) {
    return parsedTemplateTitle;
  }

  switch (pageType) {
  case 'b':
    return getBrowseTitleTag({
      pathWithQueryParams,
      categories,
      refinements,
      searchVal
    });

  case 's':
    return `Search Results for ${searchVal} at The Home Depot`;
  default:
    return 'The Home Depot';
  }
};

/**
 * Normalizes the SEO navigation parameter by ensuring it starts with 'N-'.
 *
 * @param {string} seoNavParam - The SEO navigation parameter to normalize.
 * @returns {string|null} - The normalized navigation parameter starting with 'N-',
 * or `null` if the input is falsy.
 */
export const getNormalizedNavParam = (seoNavParam) => {
  if (!seoNavParam) return null;
  return seoNavParam.startsWith('N-') ? seoNavParam : `N-${seoNavParam}`;
};

/**
 * Extracts the value following the 'N-' prefix from a canonical URL.
 *
 * @param {string} canonical - The canonical URL to extract the value from.
 * @returns {string|null} - The extracted value if found, otherwise `null`.
 *
 */
export const extractNValue = (canonical) => {
  const match = canonical?.match(/\/N-([A-Za-z0-9]+)/);
  return match ? match[1] : null;
};

/**
 * Parses an array of breadcrumb objects, optionally prepending search-related crumbs if a keyword is provided.
 *
 * @param {Array<Object>} crumbs - The breadcrumb objects to process.
 * @param {string} [keyword] - Optional search keyword to add as a breadcrumb.
 * @param {boolean} [returnAll=false] - If true, returns all crumbs; otherwise, may filter to category crumbs.
 * @returns {Array<Object>} - The normalized array of breadcrumb objects.
 */
export const parseCrumbs = (crumbs = [], keyword, returnAll = false) => {
  let result = [...(Array.isArray(crumbs) ? crumbs : [])];
  if (keyword) {
    const textSearchCrumb = {
      url: '',
      label: 'Text Search'
    };

    let label;
    try {
      label = decodeURI(keyword);
    } catch (error) {
      label = keyword;
    }
    const keywordCrumb = {
      url: `/s/${keyword}?NCNI-5`,
      label
    };
    const hasTextSearchCrumb = result.some(
      (crumb) => crumb?.label === textSearchCrumb.label
    );

    if (!hasTextSearchCrumb) {
      result = [textSearchCrumb, keywordCrumb, ...result];
    }
  }

  const isCategory = (crumb) => (crumb?.dimensionName || '').toLowerCase() === 'category';

  const categoryCrumbs = result.filter(isCategory);
  const hasMultipleCategories = categoryCrumbs.length > 1;

  const breadcrumbs = hasMultipleCategories && !returnAll
    ? categoryCrumbs
    : result.filter((crumb) => !crumb?.deselectUrl);

  return breadcrumbs.map((crumb) => normalizeBreadcrumb(crumb));
};

/**
 * Determines if a page should have a `NOINDEX/NOFOLLOW` meta tag based on query parameters and canonical URL.
 *
 * @param {string} pathWithQueryParams - The URL path with query parameters.
 * @param {string} canonicalUrl - The canonical URL of the page.
 * @returns {boolean} - Returns `true` if `NOINDEX/NOFOLLOW` should be applied, otherwise `false`.
 */
export const getAllowedNoFollow = (pathWithQueryParams, canonicalUrl) => {
  const allLowercaseQuery = pathWithQueryParams?.toLowerCase();
  const allowedUpperbound = /upperbound=(?:100|(?:2[05]|[3-6]0))0[&/]?$/;
  const upperbound = allLowercaseQuery?.split('&')?.filter(((element) => element.match(allowedUpperbound)));

  const queryParams = [
    'NCNI-5',
    'Ns=P_Topseller_Sort%7C1',
    'Ns=P_Smartsort%7C1',
    'Ns=P_REP_PRC_MODE%7C0',
    'Ns=P_REP_PRC_MODE%7C1',
    'Ns=P_Top_Rated_Sort%7C1',
    'sortorder=',
    'sortby=',
    'Ntk-',
    '/b/Featured-Products',
    'Ntt-'
  ];
  if (
    allLowercaseQuery?.includes('/b/furniture-living-room-furniture-sofas-couches')
    && (allLowercaseQuery?.includes('lowerbound=0') && upperbound?.length)) {
    return false;
  }

  return canonicalUrl?.includes('NCNI-5')
    || queryParams.some((query) => pathWithQueryParams?.includes(query))
    || allLowercaseQuery?.includes('lowerbound=')
    || allLowercaseQuery?.includes('upperbound=');
};

/**
 * Builds an Android alternate link tag from the first breadcrumb.
 *
 * @param {Array<{ label: string }>} processedBreadcrumbs - Breadcrumbs array.
 * @param {string|number} nValue - The N-Value used in the URL.
 * @returns {{ href: string, media: string } | null} The Android alternate tag or null.
 */
export const buildAndroidAlternateTag = (processedBreadcrumbs = [], nValue) => {

  if (!Array.isArray(processedBreadcrumbs) || processedBreadcrumbs.length === 0) {
    return null;
  }

  const firstLabel = processedBreadcrumbs[0].label;
  if (!firstLabel) return null;

  const formattedLabel = removeSpecialCharacters(firstLabel).replace(/\s+/g, '-');

  const href = `${ANDROID_HREF}categoryid/${nValue}/${formattedLabel}`;

  return {
    href,
    media: 'only screen and (max-width: 640px)'
  };
};
